<template lang="pug">
  PageLayout(:page="page" v-if="page" no-seo)
</template>

<script setup lang="ts">
import { onMounted } from "vue"
import { useAsyncData, type NuxtApp } from "#app"
import { getPage } from "~/services/public/Pages"
import { CATEGORY_V4_ID_HOUSE_MOVE } from "~/constants/category"
import { URL_PARAM_POSTAL_CODE, URL_PARAM_QUOTE_ID } from "~/constants/booking-insights"
import { getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"
import {
  FETCH_BOOKING_QUOTE,
  FETCH_BOOKINGS_COUNT,
  FETCH_BOOKINGS_MAP,
  FETCH_BOOKINGS_REVIEWS,
  GET_ERROR,
  SET_POSTAL_CODE,
  SET_QUOTE_ID
} from "~/store/booking-insights"
import { redirectToError } from "~/utils/v4-redirect"
import { LOCALE, LOCALE_TO_LOCAL_PATH } from "~/constants/locale"

const route = useRoute()
const configPublic = useRuntimeConfig().public
const nuxtApp = useNuxtApp()
const store = nuxtApp.$store
const snowplow = nuxtApp.$snowplow
const axios = nuxtApp.$axios
const i18n = useI18n()

const { data: page, error } = useAsyncData(async () => {
  try {
    const response = await getPage(
      configPublic,
       LOCALE_TO_LOCAL_PATH[i18n.locale.value] ?? LOCALE_TO_LOCAL_PATH[LOCALE.UK]
    )

    if (response.loading === false && response.data) {
      if (response.data.contentNode) {
        return response.data.contentNode
      } else {
        throw new Error("Page not found - data error")
      }
    }
  } catch (err) {
    throw new Error("Page not found - response error")
  }
})

onMounted(async () => {
  if (page.value) {
    snowplow.trackViewForPage(page.value)
  }

  const originalPostalCode = String(route.params[URL_PARAM_POSTAL_CODE]) || ""
  const quoteId = String(route.query[URL_PARAM_QUOTE_ID]) || ""

  let postalCode = originalPostalCode
  if (i18n.locale.value === "es") {
    postalCode = postalCode.replace(/^0/, "")
  }

  const v4Locale = getv4LocaleFromI18nLocale(i18n.locale.value)
  const params = {
    postalCode,
    category: CATEGORY_V4_ID_HOUSE_MOVE,
    locale: v4Locale
  }

  if (quoteId) {
    params["quoteId"] = quoteId
  }

  await store.dispatch(`booking-insights/${SET_POSTAL_CODE}`, originalPostalCode)
  await store.dispatch(`booking-insights/${SET_QUOTE_ID}`, quoteId)

  try {
    const payload = {
      $axios: axios,
      $config: configPublic,
      params,
      originalPostalCode
    }
    await store.dispatch(`booking-insights/${FETCH_BOOKINGS_COUNT}`, payload)
    await store.dispatch(`booking-insights/${FETCH_BOOKINGS_MAP}`, payload)
    await store.dispatch(`booking-insights/${FETCH_BOOKINGS_REVIEWS}`, payload)

    if (store.getters["booking-insights/" + GET_ERROR]) {
      redirectToError(nuxtApp.$config.public.v4Host, i18n.locale.value)
    }

    if (quoteId && typeof quoteId === "string") {
      params["quoteId"] = encodeURIComponent(quoteId)
      await store.dispatch(`booking-insights/${SET_QUOTE_ID}`, quoteId)
      await store.dispatch(`booking-insights/${FETCH_BOOKING_QUOTE}`, payload)
    }
  } catch (error) {
    redirectToError(nuxtApp.$config.public.v4Host, i18n.locale.value)
  }
})
</script>
